import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { RememberUserAuthPayload } from '@/features/auth/types/login';
import { HttpClient, buildEndpointUrl } from '@/libs/axios';
import { MutateDefaultOptions } from '@/models/api';
import { getAxiosBackendError } from '@/utils/errors';

export const getRememberUserAuthMutateKey = () => ['rememberUserAuth'];

export const getRememberUserAuthEndpoint = () => buildEndpointUrl('USERS', { additionalPaths: ['forget-password'] });

export const apiRememberUserAuth = (payload: RememberUserAuthPayload) =>
    HttpClient.post<void>(getRememberUserAuthEndpoint(), payload, { withSnackbarError: false, throwError: false });

//

export const useRememberUserAuth = () => {
    const { mutateAsync, isPending: isRemembering } = useMutation<unknown, AxiosError, RememberUserAuthPayload>({
        mutationKey: getRememberUserAuthMutateKey(),
        mutationFn: (payload) => apiRememberUserAuth(payload),
    });

    const rememberUserAuth = useCallback(
        async (payload: RememberUserAuthPayload, { onSuccess, onError, onSettled }: MutateDefaultOptions = {}) => {
            try {
                await mutateAsync(payload);
            } catch (error: unknown) {
                await onError?.(getAxiosBackendError(error, true));
                await onSettled?.();
                return;
            }

            await onSuccess?.();
            await onSettled?.();
        },
        [mutateAsync],
    );

    return { rememberUserAuth, isRemembering };
};
